import { FontAwesomeIcon, Stack, Text, ExpandableText } from "@skailer-com/uikit";
import { isAfter, startOfDay } from "date-fns";

export interface TextCellOptions<T extends object> {
  getValue: (item: T) => React.ReactNode;
  getClassName?: (item: T) => string;
  fallback?: string;
}

export interface HistoricalDateCellOptions<T extends object> extends TextCellOptions<T> {
  getDate: (item: T) => Date | null | undefined;
}

export function createTextCell<T extends object>({
  getValue,
  getClassName,
  fallback = "-",
}: TextCellOptions<T>) {
  return (cell: { row: { original: T } }) => {
    const value = getValue(cell.row.original);
    const className = getClassName?.(cell.row.original);

    return <Text className={className}>{value ?? fallback}</Text>;
  };
}

export function createExpandableTextCell<T extends object>({
  getValue,
  getClassName,
  fallback = "-",
}: TextCellOptions<T>) {
  return (cell: { row: { original: T } }) => {
    const item = cell.row.original;

    return (
      <ExpandableText
        maxLines={3}
        className={`${getClassName?.(item) ?? ""} break-word whitespace-pre-wrap`}
      >
        {getValue(item) ?? fallback}
      </ExpandableText>
    );
  };
}

export function createHistoricalDateCell<T extends object>({
  getValue,
  getClassName,
  getDate,
  fallback = "-",
}: HistoricalDateCellOptions<T>) {
  return (cell: { row: { original: T } }) => {
    const item = cell.row.original;
    const value = getValue(item);
    const className = getClassName?.(item);
    const date = getDate(item);
    const isFutureDate = date ? isAfter(startOfDay(date), startOfDay(new Date())) : false;

    return (
      <Stack className="whitespace-nowrap" direction="horizontal" items="center" gap="xs">
        {isFutureDate && <FontAwesomeIcon className="text-secondary" icon="fa-light fa-clock" />}
        <Text className={className}>{value ?? fallback}</Text>
      </Stack>
    );
  };
}
